import { Box, Chip, IconButton, InputAdornment, TextField } from "@mui/material";
import type { JSX } from "react";
import { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { CloseIcon, PlusIcon } from "@/assets";
import { LocationResolutionFilterMetadata } from "@/types/playground";

export type PostalCodeMetadataProps = {
  metadata: LocationResolutionFilterMetadata;
  postalCode: string;
  onEnterPostalCodeValue: () => void;
  setPostalCode: (postalCode: string) => void;
  onLocationResolutionMetadataChange: (metadata: LocationResolutionFilterMetadata) => void;
};

export const PostalCodeMetadata = ({
  postalCode,
  setPostalCode,
  metadata,
  onLocationResolutionMetadataChange,
  onEnterPostalCodeValue,
}: PostalCodeMetadataProps): JSX.Element => {
  const { t } = useTranslation("PlaygroundPage");

  const onDeletePostalCode = (code: string) => (): void => {
    onLocationResolutionMetadataChange({
      postalCodes: metadata.postalCodes?.filter((c) => c !== code),
    });
  };

  const handlePostalCodeChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const validInput = event.target.value.replace(/[^0-9,\s]/g, "");
    setPostalCode(validInput);
  };

  return (
    <Box sx={{ p: "15px 0px 20px 44px" }}>
      <TextField
        value={postalCode}
        onChange={handlePostalCodeChange}
        fullWidth
        variant="outlined"
        helperText={t("filter.locationResolutionGroup.enterPostalCodeInfo")}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onEnterPostalCodeValue();
          }
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton edge="end" onClick={onEnterPostalCodeValue} disabled={!postalCode}>
                <PlusIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {metadata.postalCodes && metadata.postalCodes.length > 0 && (
        <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {metadata.postalCodes.map((code) => (
            <Chip
              key={code}
              color="primary"
              label={code}
              variant="playgroundFilterChip"
              onDelete={onDeletePostalCode(code)}
              deleteIcon={<CloseIcon />}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
